import { AlertColor } from '@mui/material';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '../store/store';


const minifiedDetailViewKey = 'minifiedDetailViewKey';
const minifiedLegendKey = 'minifiedLegendKey';
const leftSideBarKey = 'showLeftSidebar';
// Define types for the slice
interface ToastMessage {
  type?: AlertColor;
  message?: string;
}

interface GraphSavingEntry {
  id: string;
  name: string;
}

interface GraphReady {
  name: string;
  correlationId: string;
  entityType: string;
}

interface UtilState {
  showLeftSidebar: boolean;
  showRightSidebar: boolean;
  toastMessage: ToastMessage;
  activeRequests: number;
  downloadProgress: number;
  componentName: string;
  showMinifiedDetailView: boolean;
  showMinifiedLegend: boolean;
  fullscreenLoading: boolean;
  graphSaving: GraphSavingEntry[];
  graphReadyQueue: GraphReady[];
}

// Define the initial state using that type
const initialState: UtilState = {
  showLeftSidebar: (sessionStorage.getItem(leftSideBarKey) === 'true') || false,
  showRightSidebar: false,
  toastMessage: {},
  activeRequests: 0,
  downloadProgress: 0,
  componentName: '',
  showMinifiedDetailView: JSON.parse(localStorage.getItem(minifiedDetailViewKey) || 'false'),
  showMinifiedLegend: JSON.parse(localStorage.getItem(minifiedLegendKey) || 'false'),
  fullscreenLoading: false,
  graphSaving: [],
  graphReadyQueue: [],
};

export const utilSlice = createSlice({
  name: 'util',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setShowRightSidebar: (state, action: PayloadAction<boolean>) => {
      state.showRightSidebar = action.payload;
    },
    setToastMessage: (state, action: PayloadAction<ToastMessage>) => {
      state.toastMessage = action.payload;
    },
    addFetch: (state) => {
      state.activeRequests++;
    },
    removeFetch: (state) => {
      state.activeRequests > 0 && state.activeRequests--;
    },
    setComponentName: (state, action: PayloadAction<string>) => {
      state.componentName = action.payload;
    },
    setShowMinifiedDetailView: (state, action: PayloadAction<boolean>) => {
      state.showMinifiedDetailView = action.payload;
      localStorage.setItem(minifiedDetailViewKey, JSON.stringify(action.payload));
    },
    setShowMinifiedLegend: (state, action: PayloadAction<boolean>) => {
      state.showMinifiedLegend = action.payload;
      localStorage.setItem(minifiedLegendKey, JSON.stringify(action.payload));
    },
    setFullscreenLoading: (state, action: PayloadAction<boolean>) => {
      state.fullscreenLoading = action.payload;
    },
    addGraphSaving: (state, action: PayloadAction<GraphSavingEntry>) => {
      state.graphSaving.push(action.payload);
    },
    removeGraphSaving: (state, action: PayloadAction<string>) => {
      state.graphSaving = state.graphSaving.filter(entry => entry.id !== action.payload);
    },
    addGraphReady: (state, action: PayloadAction<GraphReady>) => {
      state.graphReadyQueue.push(action.payload);
    },
    shiftGraphReady: (state) => {
      state.graphReadyQueue.shift();
    },
  }
});

export const {
  setShowRightSidebar,
  setToastMessage,
  addFetch,
  removeFetch,
  setComponentName,
  setShowMinifiedDetailView,
  setShowMinifiedLegend,
  setFullscreenLoading,
  addGraphSaving,
  removeGraphSaving,
  addGraphReady,
  shiftGraphReady,
} = utilSlice.actions;

// Other code such as selectors can use the imported `RootState` type
export const selectShowRightSidebar = (state: RootState): boolean => state.util.showRightSidebar;
export const selectToastMessage = (state: RootState): ToastMessage => state.util.toastMessage;
export const selectIsLoading = (state: RootState): boolean => state.util.activeRequests > 0;
export const selectComponentName = (state: RootState): string => state.util.componentName;
export const selectShowMinifiedDetailView = (state: RootState): boolean => state.util.showMinifiedDetailView;
export const selectShowMinifiedLegend = (state: RootState): boolean => state.util.showMinifiedLegend;
export const selectFullscreenLoading = (state: RootState): boolean => state.util.fullscreenLoading;
export const selectGraphSaving = (state: RootState): GraphSavingEntry[] => state.util.graphSaving;
export const selectGraphReadyQueue = (state: RootState): GraphReady[] => state.util.graphReadyQueue;
// export const selectDownloadProgress = (state: RootState): number => state.util.downloadProgress;
export default utilSlice.reducer;
